import { Button, Link, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { PasskeyEnrollmentChromeContext } from 'components/@auth/passkeyEnrollmentChrome';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import Passkey from 'services/api/Passkey';
import authUser from 'store/selectors/authUser';
import {
  continueAfterEnrollment,
  deferPasskeyEnrollment,
  getPasskeyEnrollmentDeferral,
  shouldShowPasskeyEnrollment,
} from 'utils/passkeyEnrollment';
import { I18n } from 'utils/i18n';
import { isWebAuthnSupported } from 'utils/webAuthn';
import Heading from '../Heading';

const PasskeyEnrollmentScene = () => {
  const user = useSelector(authUser);
  const location = useLocation();
  const setPasskeyInviteVisible = useContext(PasskeyEnrollmentChromeContext);
  const userRef = useRef(user);
  userRef.current = user;
  const decidedRef = useRef(false);
  const [eligible, setEligible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState(null);
  const enrollmentRef = useRef(null);
  const returnUrl = useMemo(
    () => new URLSearchParams(location.search).get('returnUrl'),
    [location.search],
  );
  const enrollmentCompanyId = useMemo(
    () => new URLSearchParams(location.search).get('passkeyEnrollmentCompanyId'),
    [location.search],
  );
  const passkeyEnrollmentHandoff = useMemo(
    () => new URLSearchParams(location.hash.slice(1)).get('passkeyEnrollmentHandoff'),
    [location.hash],
  );

  useEffect(() => {
    if (!passkeyEnrollmentHandoff || typeof window === 'undefined') {
      return;
    }
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}`,
    );
  }, [passkeyEnrollmentHandoff]);

  const continueNormally = useCallback(() => {
    continueAfterEnrollment(returnUrl, userRef.current);
  }, [returnUrl]);

  useEffect(() => {
    setPasskeyInviteVisible(eligible && !loading);
  }, [eligible, loading, setPasskeyInviteVisible]);

  useEffect(() => {
    if (!user?._id || decidedRef.current) {
      return undefined;
    }

    if (!enrollmentRef.current) {
      if (!enrollmentCompanyId || !passkeyEnrollmentHandoff) {
        decidedRef.current = true;
        continueNormally();
        return undefined;
      }
      enrollmentRef.current = { companyId: enrollmentCompanyId, passkeyEnrollmentHandoff };
    }

    let active = true;
    const supported = isWebAuthnSupported();
    const enrollment = enrollmentRef.current;

    const evaluateEligibility = async () => {
      if (!supported || !enrollment?.companyId) {
        if (active && !decidedRef.current) {
          decidedRef.current = true;
          continueNormally();
        }
        return;
      }

      try {
        const credentials = await Passkey.fetchOwnCredentials(enrollment.companyId);
        const showEnrollment = shouldShowPasskeyEnrollment({
          userId: user._id,
          loginMethod: 'sms',
          supported,
          credentialCount: credentials.length,
          deferredUntil: getPasskeyEnrollmentDeferral(user._id),
        });
        if (!active || decidedRef.current) {
          return;
        }
        decidedRef.current = true;
        if (showEnrollment) {
          setEligible(true);
          setLoading(false);
          return;
        }
        continueNormally();
      } catch {
        // The invitation is optional: inability to check it must never block access.
      }

      if (active && !decidedRef.current) {
        decidedRef.current = true;
        continueNormally();
      }
    };

    evaluateEligibility();
    return () => {
      active = false;
    };
  }, [continueNormally, enrollmentCompanyId, passkeyEnrollmentHandoff, user?._id]);

  const handleNotNow = useCallback(() => {
    deferPasskeyEnrollment(user?._id, new Date());
    continueNormally();
  }, [continueNormally, user?._id]);

  const handleRegistration = useCallback(async () => {
    const enrollment = enrollmentRef.current;
    if (
      registering ||
      !enrollment?.companyId ||
      !enrollment?.passkeyEnrollmentHandoff ||
      !user?._id
    ) {
      return;
    }
    setRegistering(true);
    setError(null);
    try {
      await Passkey.registerPasskeyWithEnrollmentHandoff({
        companyId: enrollment.companyId,
        userId: user._id,
        username: user.phone || user._id,
        passkeyEnrollmentHandoff: enrollment.passkeyEnrollmentHandoff,
      });
      continueNormally();
    } catch {
      setError(I18n.t('SettingsDrawer.Passkey_Register_Error', 'Failed to register passkey'));
    } finally {
      setRegistering(false);
    }
  }, [continueNormally, registering, user?._id, user?.phone]);

  if (!eligible || loading) {
    return null;
  }

  return (
    <Box data-testid="passkey-enrollment" component="section">
      <Heading
        type="passkey"
        headline={I18n.t('PasskeyEnrollment.headline', 'Make your next sign-in easier')}
        subheading={I18n.t(
          'PasskeyEnrollment.body',
          'Set up a passkey to sign in more securely with your fingerprint, face, or device PIN.',
        )}
      />
      {error && (
        <Typography color="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <Button
        color="primary"
        data-testid="passkey-enrollment-setup"
        disabled={registering}
        fullWidth
        onClick={handleRegistration}
        variant="contained"
      >
        {I18n.t('PasskeyEnrollment.setup', 'Set up a passkey')}
      </Button>
      <Link
        component="button"
        data-testid="passkey-enrollment-not-now"
        disabled={registering}
        onClick={handleNotNow}
        sx={{ display: 'inline-flex', mt: 2 }}
        type="button"
      >
        {I18n.t('PasskeyEnrollment.not_now', 'Not now')}
      </Link>
    </Box>
  );
};

export default PasskeyEnrollmentScene;
